'use client';

import { Children, type ReactNode, useEffect, useState } from 'react';

import styled, { css, keyframes } from 'styled-components';

type RollingProps = {
  children: ReactNode[];
  onActiveIndexChange?: (index: number) => void;
};

const useRolling = (itemsCount: number, interval: number = 2000) => {
  const [rollingIndex, setRollingIndex] = useState(0);

  useEffect(() => {
    if (itemsCount <= 1) {
      return;
    }
    const intervalRollingInteraction = setInterval(() => {
      setRollingIndex((prevIndex) => (prevIndex + 1) % itemsCount);
    }, interval);

    return () => clearInterval(intervalRollingInteraction);
  }, [itemsCount, interval]);

  return rollingIndex;
};

export const Rolling = ({ children, onActiveIndexChange }: RollingProps) => {
  const isRolling = children.length > 1;
  const rollingIndex = useRolling(children.length);
  const [isVisible, setIsVisible] = useState(false);

  // 맨처음 로드 후 이상하게 보이는 현상이 있어 0.3초간은 마지막 글자를 보여준다..
  useEffect(() => {
    setTimeout(() => {
      setIsVisible(true);
    }, 300);
  }, []);

  useEffect(() => {
    onActiveIndexChange?.(rollingIndex);
  }, [rollingIndex, onActiveIndexChange]);

  const childArray = Children.toArray(children);
  return (
    <Container>
      {!isVisible
        ? children[children.length - 1]
        : childArray.map((child, index) => (
            <RollingWrapper
              key={
                typeof child === 'object' && 'key' in child && child.key != null
                  ? child.key
                  : `rolling-wrapper-${index}`
              }
              $isRolling={isVisible && isRolling}
              $isCurrentIndex={rollingIndex === index}
            >
              {child}
            </RollingWrapper>
          ))}
    </Container>
  );
};

const moveIn = keyframes`
  from {
    transform: translateY(0%);
    opacity: 0;
  }
  to {
    transform: translateY(-50%);
    opacity: 1;
  }
`;

const moveOut = keyframes`
  from {
    transform: translateY(-50%);
    opacity: 1;
  }
  to {
    transform: translateY(-100%);
    opacity: 0;
  }
`;

const Container = styled.div`
  position: relative;
  height: 100%;
  overflow: hidden;
`;

const RollingWrapper = styled.div<{
  $isRolling: boolean;
  $isCurrentIndex: boolean;
}>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);

  ${({ $isRolling, $isCurrentIndex }) =>
    $isRolling &&
    css`
      animation: ${$isCurrentIndex ? moveIn : moveOut} 0.3s ease-in-out forwards;
    `}
`;
